/**
 * Auth Store — State Management
 *
 * Centralized state model for authentication.
 * Framework-agnostic (vanilla TS with subscriber pattern).
 */

import { apiV1Base } from '../lib/apiConfig';
const BASE_URL =
  apiV1Base();

const TOKEN_KEY = 'adversyn_auth_token';
// WO10 persist user identity (2026-06-06) — useAuthenticatedUser reads
// from this localStorage key on every render; previously nothing wrote
// to it, so the hook always returned DEFAULT_DEMO_USER ('Demo User').
const AUTH_USER_KEY = 'adversyn:auth-user';

function _persistAuthUser(user: any): void {
  if (typeof localStorage === 'undefined') return;
  if (!user || typeof user !== 'object') {
    try { localStorage.removeItem(AUTH_USER_KEY); } catch {}
    return;
  }
  // Split full_name into firstName/lastName when possible.
  const fn: string = String((user.full_name || user.fullName || '')).trim();
  const parts = fn ? fn.split(/\s+/) : [];
  const firstName: string = String(user.firstName || (parts[0] || ''));
  const lastName: string = String(user.lastName || (parts.slice(1).join(' ') || ''));
  const payload = {
    id: user.id,
    email: user.email || null,
    full_name: fn || null,
    firstName: firstName,
    lastName: lastName,
    organization: user.organization || null,
    role: user.role || null,
    account_type: user.account_type || null,
  };
  try { localStorage.setItem(AUTH_USER_KEY, JSON.stringify(payload)); } catch {}
}

// ═══════════════════════════════════════════════════════════════════════
//  STATE INTERFACE
// ═══════════════════════════════════════════════════════════════════════

export interface AuthUser {
  id: string;
  email: string;
  created_at: string;
}

export interface AuthState {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

// ═══════════════════════════════════════════════════════════════════════
//  STORE
// ═══════════════════════════════════════════════════════════════════════

type Listener = () => void;

class AuthStoreClass {
  private state: AuthState = {
    user: null,
    token: null,
    loading: false,
    error: null,
  };

  private listeners = new Set<Listener>();

  // ── Getters ──────────────────────────────────────────────────

  getState(): AuthState {
    return { ...this.state };
  }

  // ── Subscription ─────────────────────────────────────────────

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private setState(partial: Partial<AuthState>): void {
    this.state = { ...this.state, ...partial };
    this.listeners.forEach((l) => l());
  }

  // ── Auth Methods ─────────────────────────────────────────────

  async register(email: string, password: string): Promise<void> {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const envelope = await res.json();
      if (!envelope.ok) {
        throw new Error(envelope.error?.message || 'Registration failed');
      }
      const { token, user } = envelope.data;
      localStorage.setItem(TOKEN_KEY, token);
      _persistAuthUser(user);
      this.setState({ user, token, loading: false, error: null });
    } catch (err) {
      this.setState({ loading: false, error: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  }

  async login(email: string, password: string): Promise<void> {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const envelope = await res.json();
      if (!envelope.ok) {
        throw new Error(envelope.error?.message || 'Login failed');
      }
      const { token, user } = envelope.data;
      localStorage.setItem(TOKEN_KEY, token);
      _persistAuthUser(user);
      this.setState({ user, token, loading: false, error: null });
    } catch (err) {
      this.setState({ loading: false, error: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  }

  async logout(): Promise<void> {
    const token = this.state.token || localStorage.getItem(TOKEN_KEY);
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
    } catch {
      // Best-effort — clear local state regardless
    }
    localStorage.removeItem(TOKEN_KEY);
    try { localStorage.removeItem(AUTH_USER_KEY); } catch {}
    this.setState({ user: null, token: null, loading: false, error: null });
  }

  async checkSession(): Promise<void> {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      this.setState({ user: null, token: null, loading: false });
      return;
    }
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${BASE_URL}/auth/me`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      const envelope = await res.json();
      if (!envelope.ok) {
        localStorage.removeItem(TOKEN_KEY);
        this.setState({ user: null, token: null, loading: false });
        return;
      }
      _persistAuthUser(envelope.data);
      this.setState({ user: envelope.data, token, loading: false, error: null });
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      try { localStorage.removeItem(AUTH_USER_KEY); } catch {}
      this.setState({ user: null, token: null, loading: false });
    }
  }

  init(): void {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      this.setState({ token });
      this.checkSession();
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════
//  SINGLETON EXPORT
// ═══════════════════════════════════════════════════════════════════════

export const authStore = new AuthStoreClass();
export default authStore;

// ═══════════════════════════════════════════════════════════════════════
//  DEMO-MODE HELPER (referenced by App.tsx::TradingUiDemoModeBanner)
//
//  Backfilled 2026-06-05 as part of the demo-user / broker-isolation work.
//  The App.tsx import had been dangling since the source-sync gap
//  documented in project_trading_ui_source_sync_required (2026-05-19).
//  This stub matches the expected semantics: TRUE when there is no
//  ``adversyn_auth_token`` in localStorage (i.e. anonymous demo view).
//  It is intentionally NOT a hook so it can be referenced from helper
//  scopes; React components should prefer ``useAuthenticatedUser()``.
// ═══════════════════════════════════════════════════════════════════════
export function isTradingUiDemoMode(): boolean {
  try {
    if (typeof localStorage === 'undefined') return true;
    return !localStorage.getItem('adversyn_auth_token');
  } catch {
    return true;
  }
}
